import { AdditionalPoisModule } from '@/store/modules';
import {UserActivityCategoryEnum} from '@mobi-components/global-solution';
import {computed} from '@vue/composition-api';

export function truncate(str: string, n: number, useWordBoundary: boolean) {
  if (str.length <= n) {
    return str;
  }
  const subString = str.substr(0, n - 1); // the original check
  return (useWordBoundary ? subString.substr(0, subString.lastIndexOf(' ')) : subString) + '...';
}

export interface PlaceCardProps {
  activity: PlaceCardItem;
  isLoading: boolean;
  labelText?: string;
  descriptionText?: string;
}

export interface PlaceCardLocation {
  address: string;
}

export interface SimpleTag {
  name: string;
  isActive: boolean;
}

export interface PlaceCardItem {
  description?: string;
  location?: PlaceCardLocation;
  explanations?: string[];
  name?: string;
  img?: string;
  tags?: SimpleTag[];
  rating?: number;
  price?: string;
  isFlexible?: boolean;
  isActivity?: boolean;
  category?: UserActivityCategoryEnum;
  isRecommendation?: boolean;
  id: string;
  isStay?: boolean;
  scheduled_start_time?: number;
  scheduled_end_time?: number;
  children?: string[];
  parent?: string;
  isSaved?: boolean;
  isChild?:boolean;
  isTopLevelParent?: boolean;
}

export function usePlaceCardLogic(props: PlaceCardProps) {
  const isLoading = computed(() => {
    return props.isLoading || false;
  });
  const description = computed(() => {
    const s = truncate(props.activity?.description || '', 200, true);
    const address = props.activity?.location?.address || '';
    const exp = (props.activity?.explanations || []).join(', ');
    return isLoading.value ? '' : s || exp || address;
  });
  const address = computed(() => {
    const s = truncate(props.activity?.description || '', 200, true);
    const address = props.activity?.location?.address || '';
    const exp = (props.activity?.explanations || []).join(', ');
    return isLoading.value ? '' : address || s || exp;
  });

  const hasChildren = computed(() => {
    return props.activity?.isTopLevelParent;
  });

  const hasParent = computed(() => {
    return !!props.activity?.parent;
  });

  const isChildActivity = computed(() => {
    return !!props.activity?.isChild;
  });

  const explanations = computed(() => {
    if (isLoading.value) {
      return '';
    }

    if (props.activity?.isFlexible) {
      return `<b>Flexible:</b> best spot in the itinerary for now`;
    }

    if (props.activity?.isRecommendation) {
      return `<b>Suggested Activity</b>`;
    }
    return props.descriptionText || (props.activity?.explanations || []).join(', ');
  });

  const activityTime = computed(() => {
    if (!props.activity?.scheduled_start_time || !props.activity?.scheduled_end_time) {
      return '';
    }

    const start = new Date(props.activity?.scheduled_start_time * 1000);
    const end = new Date(props?.activity?.scheduled_end_time * 1000);

    const timeOptions = {
      hourCycle: 'h23',
      hour: '2-digit',
      minute: '2-digit',
    } as const;

    const formatter = new Intl.DateTimeFormat('en-US', {
      // timeZone: 'Asia/Singapore',
      ...timeOptions,
    });

    return `${formatter.format(start)}-${formatter.format(end)}`;
  });

  const durationTime = computed(() => {
    if (!props.activity?.scheduled_start_time || !props.activity?.scheduled_end_time) {
      return '';
    }

    const diff = props?.activity?.scheduled_end_time - props.activity?.scheduled_start_time;

    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const fixedMinutes = minutes.toString().length > 1 ? `${minutes.toString()}m` : `0${minutes.toString()}m`;
    const fixedHours = hours > 0 ? `${hours}h ` : '';

    return `${fixedHours}${fixedMinutes}`;
  });

  const isFlexible = computed(() => {
    return props.activity?.isFlexible || false;
  });
  const activityName = computed(() => {
    if (isLoading.value) {
      return '';
    }
    return props.activity?.name || '';
  });
  const activityImg = computed(() => {
    if (isLoading.value) {
      return '';
    }

    return props.activity?.img || '';
  });
  const activityTags = computed(() => {
    if (isLoading.value) {
      return [];
    }

    const included = new Set();

    const uniqueTags = (props.activity?.tags || [])
      .filter((x) => {
        if (included.has(x.name)) {
          return false;
        }
        included.add(x.name);
        return true;
      })
      .slice(0, 5);
    return uniqueTags;
  });
  const rating = computed(() => {
    if (isLoading.value) {
      return 0;
    }

    return (props.activity?.rating || 0).toFixed(1);
  });

  const price = computed(() => {
    if (isLoading.value || !props.activity?.price) {
      return '';
    }

    // TODO: At the moment all prices are in format "$123", don't need formatting
    const rawPrice = props.activity?.price;
    return rawPrice;

    // const priceAsNumber = Number(rawPrice);
    // if (Number.isNaN(priceAsNumber)) {
    //   return rawPrice;
    // }

    // const formattedPrice =  `$${priceAsNumber.toFixed()}`;
    // return formattedPrice;
  });

  const isRecommendation = computed(() => {
    if (isLoading.value) {
      return false;
    }

    return props.activity?.isRecommendation || false;
  });
  const isSavedPoi = computed(() => {
    if (isLoading.value) {
      return false;
    }

    return props.activity?.isSaved || false;
  });
  const formattedName = computed(() => {
    const name = (props.activity?.name || '')
      .replace('LUNCH:', '')
      .replace('BREAKFAST:', '')
      .replace('DINNER:', '')
      .trim();
    const type = (props.activity?.name || '')
      .replace(name, '')
      .replace(':', '')
      .trim()
      .toLowerCase();
    const at = type.length ? 'at' : '';
    if (isLoading.value) {
      return '';
    }

    return (
      props.labelText ||
      `${type && type.length ? type.charAt(0).toUpperCase() + type.slice(1) : ''} ${at} <b>${name}</b>`.trim()
    );
  });
  const backgroundImage = computed(() => {
    if (isLoading.value) {
      return '';
    }

    return `url('${activityImg.value}')`;
  });

  const isStay = computed(() => {
    return props.activity?.isStay || false;
  });

  const childPois = computed(() => {
    return (props.activity?.children || []).map((child) => AdditionalPoisModule.getPoiById(child));
  });

  const parentPoi = computed(() => {
    return AdditionalPoisModule.getPoiById(props.activity?.parent || '');
  });

  const allPois = computed(() => {
    return [parentPoi.value, ...childPois.value].filter(Boolean)
    .map((x) => ({
      img: x?.images?.small,
      name: x?.name,
      id: x?.id,
    }))
    .filter((x) => x.id !== props.activity?.id);
  });


  return {
    description,
    address,
    activityName,
    activityImg,
    activityTags,
    rating,
    price,
    formattedName,
    backgroundImage,
    explanations,
    isFlexible,
    isRecommendation,
    isSavedPoi,
    isStay,
    activityTime,
    hasChildren,
    hasParent,
    isChildActivity,
    allPois,
    durationTime,
  };
}
